import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, FileWarning, Building2, FileDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OptionValuationView } from "@/components/equity/OptionValuationView";
import { ExercisePlanner } from "@/components/equity/ExercisePlanner";
import { EquityQuestions } from "@/components/equity/EquityQuestions";
import { money } from "@/components/equity/AssumptionsPanel";
import api, { API } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

const seedAssumptions = (v) => ({ ...v.assumptions, exit_values: v.scenarios.map((s) => Math.round(s.exit_value)) });

export default function EmployeeEquity() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [valuation, setValuation] = useState(null);
  const [assumptions, setAssumptions] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/equity/${token}`)
      .then(({ data }) => { setData(data); setValuation(data.valuation); setAssumptions(seedAssumptions(data.valuation)); })
      .catch((e) => setError(e?.response?.data?.detail || "This equity link is invalid or was revoked"));
  }, [token]);

  const changeAssumptions = (next) => {
    setAssumptions(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.post(`/equity/${token}/valuation`, { assumptions: next }); setValuation(data); }
      catch (e) { /* keep last good valuation */ }
      finally { setBusy(false); }
    }, 350);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="equity-logo"><Logo /></Link>
          <span className="text-xs text-muted-foreground">Private equity page · estimates only</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        {error ? (
          <div className="mx-auto max-w-md rounded-xl border bg-card p-8 text-center" data-testid="equity-error">
            <FileWarning className="mx-auto h-8 w-8 text-warning" />
            <h1 className="mt-3 h-display text-xl font-semibold text-foreground">Link not available</h1>
            <p className="mt-1 text-sm text-muted-foreground">{error}. Ask your company for a fresh link.</p>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4" data-testid="equity-header">
              <div>
              <div data-testid="equity-company-name" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-ink"><Building2 className="h-3.5 w-3.5" /> {data.company.name}</div>
                <h1 className="mt-1 h-display text-3xl font-semibold text-foreground sm:text-4xl">{data.grant.holder_name ? `${data.grant.holder_name.split(" ")[0]}, here's your equity.` : "Your equity"}</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                  {Math.round(data.grant.options).toLocaleString()} {data.grant.option_type} options at a {money(data.grant.strike, 2)} strike{data.grant.issued_on ? `, granted ${formatDate(data.grant.issued_on)}` : ""}.
                  Company figures come from {data.company.name}'s cap table{data.company.fmv_date ? ` (409A dated ${formatDate(data.company.fmv_date)})` : ""}; the scenario assumptions below are yours to change.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-normal" data-testid="equity-fmv">409A FMV {valuation.company.fmv ? money(valuation.company.fmv, 2) : "not set"}</Badge>
                <Badge variant="secondary" className="font-normal" data-testid="equity-pref">Preferred {valuation.company.preferred_price ? money(valuation.company.preferred_price, 2) : "n/a"}</Badge>
                <Badge variant="secondary" className="font-normal">FD shares {valuation.company.fd_shares ? Math.round(valuation.company.fd_shares).toLocaleString() : "n/a"}</Badge>
                <Button asChild size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
                  <a href={`${API}/equity/${token}/statement.pdf`} target="_blank" rel="noreferrer" data-testid="equity-statement-pdf"><FileDown className="h-3.5 w-3.5" /> Download statement (PDF)</a>
                </Button>
              </div>
            </div>
            <OptionValuationView valuation={valuation} assumptions={assumptions} onAssumptionsChange={changeAssumptions} loading={busy}>
              <ExercisePlanner defaultFmv={valuation.company.fmv} deps={[assumptions?.tax_mode, assumptions?.simple_rate_pct, assumptions?.amt_rate_pct, assumptions?.ordinary_rate_pct, assumptions?.state_rate_pct]}
                compute={(p) => api.post(`/equity/${token}/exercise-plan`, { ...p, assumptions }).then((r) => r.data)} />
              <EquityQuestions token={token} companyName={data.company.name} initial={data.questions || []} />
            </OptionValuationView>
          </>
        )}
      </main>
      <TenXBrandFooter context="employee-equity" />
    </div>
  );
}
